'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { X, Bell, ChevronDown, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { getActiveAnnouncements, dismissAnnouncement, type Announcement } from '@/lib/api-client';

export default function AnnouncementBanner() {
  const { user } = useAuth();
  const [banners, setBanners] = useState<Announcement[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<Set<string>>(new Set());

  const fetchAnnouncements = useCallback(() => {
    if (!user) return;
    getActiveAnnouncements()
      .then((all) => setBanners(all.filter((a) => a.displayType === 'banner')))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetchAnnouncements();
    const id = setInterval(fetchAnnouncements, 5 * 60_000);
    return () => clearInterval(id);
  }, [fetchAnnouncements]);

  const handleDismiss = async (id: string) => {
    setDismissing((s) => new Set(s).add(id));
    try {
      await dismissAnnouncement(id);
      setBanners((prev) => prev.filter((b) => b.id !== id));
    } catch { /* ignore */ }
    setDismissing((s) => { const n = new Set(s); n.delete(id); return n; });
  };

  if (!banners.length) return null;

  return (
    <>
      {banners.map((b) => {
        const isExpanded = expandedId === b.id;
        const hasBody = b.bodyType === 'image' ? !!b.body : !!b.body.trim();
        return (
          <div key={b.id} className="bg-sky-500/10 border-b border-sky-500/20">
            <div className="max-w-7xl mx-auto px-2">
              {/* Collapsed header */}
              <div className="flex items-center gap-3 py-2">
                <Bell size={14} className="text-sky-400 flex-shrink-0" />
                <button
                  onClick={() => hasBody && setExpandedId(isExpanded ? null : b.id)}
                  className={cn('flex-1 text-left text-sm font-medium text-sky-300 truncate', hasBody && 'cursor-pointer hover:text-sky-200')}
                >
                  {b.title}
                </button>
                {b.ctaUrl && !isExpanded && (
                  <a
                    href={b.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden sm:flex items-center gap-1 text-xs font-semibold text-sky-400 hover:text-sky-300 bg-sky-500/10 px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
                  >
                    {b.ctaLabel || 'Learn more'} <ExternalLink size={10} />
                  </a>
                )}
                {hasBody && (
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : b.id)}
                    className="p-1 text-sky-400/50 hover:text-sky-300 transition-colors flex-shrink-0"
                  >
                    <ChevronDown size={14} className={cn('transition-transform', isExpanded && 'rotate-180')} />
                  </button>
                )}
                <button
                  onClick={() => handleDismiss(b.id)}
                  disabled={dismissing.has(b.id)}
                  className="p-1 text-sky-400/40 hover:text-sky-300 transition-colors flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Expanded body */}
              {isExpanded && hasBody && (
                <div className="pb-3 pt-0.5 border-t border-sky-500/10">
                  {b.bodyType === 'image' ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={b.body} alt={b.title} className="max-h-64 rounded-lg mt-2" />
                  ) : (
                    <p className="text-xs text-slate-300 whitespace-pre-wrap mt-1.5 leading-relaxed">{b.body}</p>
                  )}
                  {b.ctaUrl && (
                    <a
                      href={b.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-sky-400 hover:text-sky-300 bg-sky-500/10 px-3 py-1.5 rounded-lg mt-2 transition-colors"
                    >
                      {b.ctaLabel || 'Learn more'} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
